import { mbTextStyles } from '@mightybyte/rnw.utils.style-utils';
import React, { useState } from 'react';
import {
    StyleProp,
    StyleSheet,
    TextStyle,
    TouchableOpacity,
    View,
    Text,
} from 'react-native';
import {
    MB_TextInput,
    MB_TextInputInputType,
} from '@mightybyte/rnw.components.text-input';
import DateTimePicker, { DateType } from 'react-native-ui-datepicker';
import { MB_Modal } from '@mightybyte/rnw.components.modal';
import { format } from 'date-fns';
import { MB_Button } from '@mightybyte/rnw.components.button';
import { getColors } from '../../constants/colors';
import { textStyles } from '../../constants/textStyles';

const COLORS = getColors();

type TimePickerProps = {
    time?: Date;
    onTimeChange?: (time: Date) => void;
    title?: string;
    titleStyle?: StyleProp<TextStyle>;
    showRequiredAsteriks?: boolean;
    isError?: boolean;
    errorMessage?: string;
    disabled?: boolean;
    use24Hour?: boolean;
    minTime?: Date;
    maxTime?: Date;
};

const TimePicker = ({
    time,
    onTimeChange,
    title,
    titleStyle,
    showRequiredAsteriks,
    isError,
    disabled,
    use24Hour = false,
}: TimePickerProps) => {
    const [isModalVisible, setIsModalVisible] = useState(false);
    const [selectedTime, setSelectedTime] = useState<DateType>(
        time || new Date(),
    );

    const showTimePicker = () => {
        setSelectedTime(time || new Date());
        setIsModalVisible(true);
    };

    const onClose = () => setIsModalVisible(false);

    const onChange = (params: { date: DateType }) => {
        if (params.date) {
            setSelectedTime(params.date);
        }
    };

    const onSave = () => {
        if (selectedTime && onTimeChange) {
            let timeDate: Date;

            if (selectedTime instanceof Date) {
                timeDate = selectedTime;
            } else if (
                typeof selectedTime === 'object' &&
                selectedTime !== null &&
                'toDate' in selectedTime
            ) {
                timeDate = (selectedTime as { toDate: () => Date }).toDate();
            } else if (typeof selectedTime === 'string') {
                timeDate = new Date(selectedTime);
            } else if (typeof selectedTime === 'number') {
                timeDate = new Date(selectedTime);
            } else {
                console.warn('Failed to convert selected time to Date object');
                timeDate = new Date();
            }

            onTimeChange(timeDate);
        }
        onClose();
    };

    const formatTime = (time?: Date) => {
        if (!time) return undefined;

        return use24Hour ? format(time, 'HH:mm') : format(time, 'h:mm a');
    };

    const formattedTime = formatTime(time);

    return (
        <React.Fragment>
            <View>
                <MB_TextInput
                    title={title}
                    showRequiredAsteriks={showRequiredAsteriks}
                    placeholder={use24Hour ? '00:00' : '00:00 AM'}
                    titleStyle={titleStyle}
                    style={[styles.timeInput, disabled ? styles.disabled : {}]}
                    inputType={MB_TextInputInputType.numbers}
                    value={formattedTime}
                    disable
                    isError={isError}
                />
                <TouchableOpacity
                    style={styles.pressable}
                    activeOpacity={1}
                    onPress={showTimePicker}
                    disabled={disabled}
                />
            </View>

            <MB_Modal
                key={`${isModalVisible}`}
                isVisible={isModalVisible}
                hideCloseButton
                childrenWrapperStyle={styles.modal}
                withoutScrollView={true}
                backdropOpacity={0.3}
                onBackdropPress={onClose}
            >
                <View style={styles.modalHeader}>
                    <Text style={styles.modalTitle}>
                        {title || 'Select Time'}
                    </Text>
                </View>

                <View style={styles.timePickerContainer}>
                    <DateTimePicker
                        mode="single"
                        date={selectedTime}
                        onChange={onChange}
                        timePicker={true}
                        selectedItemColor={COLORS.orange}
                        headerTextStyle={styles.calendarText}
                        initialView="time"
                    />
                </View>

                <View style={styles.buttonContainer}>
                    <MB_Button
                        title="OK"
                        onPress={onSave}
                        style={styles.okButton}
                        textStyle={styles.buttonText}
                    />
                    <View style={styles.buttonSpacer} />
                    <MB_Button
                        title="CANCEL"
                        onPress={onClose}
                        style={styles.cancelButton}
                        textStyle={styles.cancelButtonText}
                    />
                </View>
            </MB_Modal>
        </React.Fragment>
    );
};

export default TimePicker;

const styles = StyleSheet.create({
    modal: {
        paddingTop: 0,
        paddingBottom: 20,
        paddingLeft: 0,
        paddingRight: 0,
        width: 350,
        overflow: 'hidden',
    },
    modalHeader: {
        backgroundColor: COLORS.orange,
        width: '100%',
        paddingVertical: 15,
        paddingHorizontal: 20,
        marginBottom: 10,
    },
    modalTitle: mbTextStyles([
        textStyles.normalText,
        {
            fontSize: 18,
            fontWeight: '600',
            color: '#FFFFFF',
        },
    ]),
    timePickerContainer: {
        paddingHorizontal: 20,
        paddingBottom: 20,
    },
    timeInput: {
        maxWidth: 200,
        minHeight: 51,
        paddingHorizontal: 10,
    },
    pressable: {
        position: 'absolute',
        width: '100%',
        height: '65%',
        bottom: 0,
    },
    buttonContainer: {
        flexDirection: 'row',
        paddingHorizontal: 20,
        justifyContent: 'center',
    },
    okButton: {
        flex: 1,
        backgroundColor: COLORS.orange,
    },
    cancelButton: {
        flex: 1,
        backgroundColor: '#FFFFFF',
        borderWidth: 1,
        borderColor: COLORS.orange,
    },
    buttonSpacer: {
        width: 10,
    },
    buttonText: mbTextStyles([
        textStyles.normalText,
        {
            color: '#FFFFFF',
            fontSize: 16,
        },
    ]),
    cancelButtonText: mbTextStyles([
        textStyles.normalText,
        {
            color: COLORS.orange,
            fontSize: 16,
        },
    ]),
    calendarText: {
        color: '#757575',
    },
    disabled: {
        backgroundColor: '#F2F2F2',
    },
});
